// Patient chat locations (9928: 1 Odessa, 2 Frisco, 3 CODE). Since 11-04 every location is its own
// chat partition on the server: a conversation (chat_master row) carries the departmentid of the
// inbox it is in, a location-scoped login lists only its own location's conversations, and an
// all_departments login lists the location picked in the header (or every location on "All").
//
// A staff reply is stamped with the inbox it is written in. The server already knows that for a
// location-scoped login (its own location); for an all_departments login it needs the
// conversation's departmentid, which the reply sends (schat `departmentid`).
import { useContext } from "react";
import { ReactReduxContext } from "react-redux";
import { hasMultipleDepartments, offeredDepartmentId, practiceDepartmentName, practiceFeatures } from "../../config/practice";
import { ALL_LOCATIONS, canViewAllLocations, viewsAllLocations, workingLocation } from "../../utils/workingLocation";

// The departmentid a staff reply in `conversation` (an inbox row) sends, or null (one-location
// practice, or a chat opened without an inbox row: the server then decides).
export function replyDepartmentId(conversation) {
  if (!practiceFeatures().workingLocations) return null;
  return offeredDepartmentId(conversation?.departmentid);
}

// The location name shown on an inbox row, only while the login views every location ("All"),
// where conversations of several locations are listed together; null otherwise.
export function chatLocationLabel(conversation, config) {
  if (!practiceFeatures().workingLocations || !hasMultipleDepartments()) return null;
  if (!viewsAllLocations(config)) return null;
  const id = offeredDepartmentId(conversation?.departmentid);
  return id ? practiceDepartmentName(id, null) : null;
}

// The login's user config from the redux store when there is one (the app always has it; a list
// rendered without a store, e.g. in a test, gets undefined = no location tags). Read at render:
// the chat list re-renders on every fetch, and a header location switch reloads the page.
export function useChatUserConfig() {
  const ctx = useContext(ReactReduxContext);
  return ctx?.store ? ctx.store.getState()?.userconfig?.config : undefined;
}

// ── Thread view "This location" / "All locations" (owner 11-04) ──────────────────────────────
// A staff thread shows the login's location by default (the server keeps CODE apart). "All
// locations" asks the thread routes for every location's rows of the patient (all_locations),
// in time order, each tagged with its location. The composer stays on the current location: a
// reply still sends replyDepartmentId(conversation), whichever view is on. Remembered per browser.
export const THREAD_THIS = "this";
export const THREAD_ALL = "all";
export const THREAD_SCOPE_KEY = "care.chatThreadScope";

// The toggle exists only where the practice has several locations (9928); not on OSA.
export function threadScopeAvailable() {
  return Boolean(practiceFeatures().workingLocations) && hasMultipleDepartments();
}

export function readThreadScope() {
  if (!threadScopeAvailable()) return THREAD_THIS;
  try {
    return window.localStorage.getItem(THREAD_SCOPE_KEY) === THREAD_ALL ? THREAD_ALL : THREAD_THIS;
  } catch (e) {
    return THREAD_THIS;
  }
}

export function writeThreadScope(scope) {
  try {
    window.localStorage.setItem(THREAD_SCOPE_KEY, scope === THREAD_ALL ? THREAD_ALL : THREAD_THIS);
  } catch (e) {
    // private window / blocked storage: the choice lasts for this page only
  }
}

export const showsAllLocations = (scope) => scope === THREAD_ALL && threadScopeAvailable();

// Extra body fields of POST messaging/chatdetails for this view ({} = today's request).
export const threadScopeBody = (scope) => (showsAllLocations(scope) ? { all_locations: true } : {});

// Query string of the GET thread routes (chat-dates) for this view ("" = today's request).
export const threadScopeQuery = (scope) => (showsAllLocations(scope) ? "?all_locations=true" : "");

// The location tag of one message in the "All locations" view, else null.
export function messageLocationLabel(message, scope) {
  if (!showsAllLocations(scope)) return null;
  const id = offeredDepartmentId(message?.departmentid);
  return id ? practiceDepartmentName(id, null) : null;
}

// The location a reply typed now goes to, as a name ("CODE"), or null when the server decides
// (an untagged login, or an all_departments login on "All" with a chat opened without an inbox
// row). Mirrors the gateway's _dept_from_user: a location-scoped login always replies in its own
// location; an all_departments login in the conversation's, else the header's.
export function replyLocationLabel(conversation, config) {
  if (!threadScopeAvailable()) return null;
  let id = null;
  if (canViewAllLocations(config)) {
    id = replyDepartmentId(conversation);
    if (!id) {
      const loc = workingLocation(config);
      id = loc === ALL_LOCATIONS ? null : offeredDepartmentId(loc);
    }
  } else {
    id = offeredDepartmentId(config?.departmentid);
  }
  return id ? practiceDepartmentName(id, null) : null;
}

export const replyHintText = (conversation, config) => {
  const name = replyLocationLabel(conversation, config);
  return name ? `Replying in ${name}` : "Replying in this location's thread";
};
